<i18n>
[
    "reviews__stars--label",
    "reviews__small--label",
    "reviews__true--label",
    "reviews__large--label"
]
</i18n>

<template>
    <div class="c-bar-graph">
        <div class="c-bar-graph__content-wrapper">
            <div
                :class="{
                    'c-bar-graph__bar': true,
                    'c-bar-graph__fit-bar': barGraphTypeOptions.useTypeFit,
                }"
                :style="barStyles"
            >
            </div>
            <template v-if="barGraphTypeOptions.useTypeStars">
                <div v-for="index in (MAX_RATING - 1)" :key="`bar-graph-${index}`">
                    <span class="c-bar-graph__bar-border" :style="barBorderStyles(index-1)"></span>
                </div>
                <div v-for="index in MAX_RATING" :key="`bar-graph-label-${index}`">
                    <span
                        class="c-bar-graph__bar-border c-bar-graph__star-bar-label o-text--label u-text--gray"
                        :style="barBorderStyles(index-1)"
                    >
                        {{ $tc('reviews__stars--label', index) }}
                    </span>
                </div>
            </template>
            <template v-if="barGraphTypeOptions.useTypeFit">
                <div class="c-bar-graph__fit-bar-label o-text--label u-text--gray">
                    <span
                        :class="{
                            'o-text--caption': smallBarGraph,
                            'c-bar-graph__fit-bar-small': true,
                        }"
                    >
                        {{ $t('reviews__small--label') }}
                    </span>
                    <span
                        :class="{
                            'o-text--caption': smallBarGraph,
                            'c-bar-graph__fit-bar-true': true,
                        }"
                    >
                        {{ $t('reviews__true--label') }}
                    </span>
                    <span
                        :class="{
                            'o-text--caption': smallBarGraph,
                            'c-bar-graph__fit-bar-large': true,
                        }"
                    >
                        {{ $t('reviews__large--label') }}
                    </span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { MAX_RATING } from '~coreModules/core/js/product-constants';
import { barGraphTypesMap, BAR_GRAPH_TYPES, BAR_GRAPH_RATING } from '~modules/core/js/bar-graph-constants';

export default {
    name: 'BarGraph',
    props: {
        barType: {
            type: String,
            default: BAR_GRAPH_RATING,
            validator: value => BAR_GRAPH_TYPES.indexOf(value) !== -1,
        },
        ratingAverage: {
            type: Number,
            required: true,
        },
        smallBarGraph: {
            type: Boolean,
            default: false,
        },
        fitBarWidth: {
            type: String,
            default: '48px',
            validator(size) {
                return ['48px', '80px'].includes(size);
            },
        },
    },
    data: () => ({
        MAX_RATING,
        barGraphTypesMap,
    }),
    computed: {
        barGraphTypeOptions() {
            return this.barGraphTypesMap[this.barType];
        },
        barStyles() {
            if (this.barGraphTypeOptions.useTypeFit && this.ratingAverage === 0) {
                return null;
            }
            if (this.barGraphTypeOptions.useTypeFit) {
                return {
                    left: this.fitBarMarginLeft,
                    '--fitBarWidth': this.fitBarWidth,
                };
            }
            return {
                width: `${this.normalBarWidthPercentage}%`,
            };
        },
        fitBarMarginLeft() {
            if (this.ratingAverage < 1) {
                return 0;
            }

            if (this.ratingAverage > 5) {
                return `calc(100% - ${this.fitBarWidth})`;
            }

            return `calc(calc(calc(100% - ${this.fitBarWidth}) / 4) * ${(this.ratingAverage - 1)})`;
        },
        normalBarWidthPercentage() {
            return (this.ratingAverage / 5) * 100;
        },
    },
    methods: {
        barBorderStyles(index) {
            return {
                left: `${(index * 20)}%`,
            };
        },
    },
};
</script>

<style lang="scss">
    .c-bar-graph {
        $text-margin-top: $nu-spacer-1pt5;

        height: $nu-spacer-3;

        &__content-wrapper {
            position: relative;
            background-color: $nu-gray--medium;
            width: 100%;
            height: $nu-spacer-0pt5;
        }

        &__bar {
            position: absolute;
            background-color: $nu-primary;
            width: 100%;
            height: $nu-spacer-0pt5;
        }

        &__bar-border {
            position: absolute;
            width: 20%;
            height: $nu-spacer-0pt5;
            border-right: 1px solid $nu-white;
        }

        &__star-bar-label {
            text-align: center;
            border-right: none;
            height: auto;
            margin-top: $text-margin-top;
        }

        &__fit-bar {
            width: var(--fitBarWidth);
            height: $nu-spacer-1;
            margin-top: -2px;
        }

        &__fit-bar-label {
            width: 100%;
            height: $nu-spacer-0pt5;
        }

        &__fit-bar-small,
        &__fit-bar-true,
        &__fit-bar-large {
            position: absolute;
            height: auto;
            margin-top: $text-margin-top;
        }

        &__fit-bar-small {
            text-align: start;
            left: 0;
            width: 30%;
        }

        &__fit-bar-true {
            text-align: center;
            left: 30%;
            width: 40%;
        }

        &__fit-bar-large {
            text-align: end;
            width: 30%;
            left: 70%;
        }
    }
</style>
